import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  TrashIcon,
  KeyIcon,
  PencilIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { getUserTypeLabel, getUserTypeBadgeClasses } from "../utils/users";
import { deleteUser as deleteUserApi } from "../utils/usersApi";

function Users() {
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrg, setSelectedOrg] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [orgSearchTerm, setOrgSearchTerm] = useState("");

  // Update User dialog state
  const [showTypeDialog, setShowTypeDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Delete User dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [newUserType, setNewUserType] = useState("");
  const [newOrganizationId, setNewOrganizationId] = useState("");
  const [dialogOrgSearchTerm, setDialogOrgSearchTerm] = useState("");
  const [showDialogOrgDropdown, setShowDialogOrgDropdown] = useState(false);

  // Fetch organizations for the filter dropdown
  useEffect(() => {
    const fetchOrganizations = async () => {
      setOrgsLoading(true);
      try {
        const orgsRef = collection(db, "organizations");
        const q = query(orgsRef, orderBy("name"));
        const snapshot = await getDocs(q);
        const orgsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name,
        }));
        setOrganizations(orgsList);

        // Check for URL parameter to auto-filter by organization
        const urlParams = new URLSearchParams(location.search);
        const orgParam = urlParams.get("org");
        if (orgParam) {
          setSelectedOrg(orgParam);
          setOrgSearchTerm(
            orgsList.find((org) => org.id === orgParam)?.name || ""
          );
        }
      } catch (error) {
        console.error("Error fetching organizations:", error);
        toast.error("Error loading organizations");
      } finally {
        setOrgsLoading(false);
      }
    };

    fetchOrganizations();
  }, [location.search]);

  // Use useMemo for efficient filtering without state updates
  const filteredUsers = useMemo(() => {
    if (!users.length) return [];

    let filtered = users;

    // Filter by email
    if (searchTerm.trim() !== "") {
      filtered = filtered.filter((user) =>
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by organization
    if (selectedOrg && selectedOrg !== "") {
      filtered = filtered.filter((user) => {
        if (user.organization_id && user.organization_id.path) {
          const orgId = user.organization_id.path.split("/").pop();
          return orgId === selectedOrg;
        }
        return false;
      });
    }

    // Only return results if there are active filters
    if (searchTerm.trim() !== "" || (selectedOrg && selectedOrg !== "")) {
      return filtered;
    } else {
      return []; // Show empty list when no filters
    }
  }, [users, searchTerm, selectedOrg]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersRef = collection(db, "users");
        const q = query(usersRef, orderBy("created_time", "desc"));
        const querySnapshot = await getDocs(q);

        const usersList = querySnapshot.docs.map((docSnap) => {
          const userData = docSnap.data();
          return {
            id: docSnap.id,
            ...userData,
            // Remove organizationName since we're not using it anymore
          };
        });

        setUsers(usersList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.error("Error loading users");
        setLoading(false);
      }
    };

    fetchUsers();
  }, [selectedOrg]); // Removed applyFilters dependency since we're using useMemo now

  const handleSearch = (value) => {
    setSearchTerm(value);
    // Remove the direct call to applyFilters - let the useEffect handle it
  };

  // Get organization name for display
  const getOrganizationName = (user) => {
    if (!user.organization_id || !user.organization_id.path) return "--";
    const orgId = user.organization_id.path.split("/").pop();
    const org = organizations.find((o) => o.id === orgId);
    return org ? org.name : "--";
  };

  // Filter organizations for autocomplete
  const filteredOrganizations = organizations.filter((org) =>
    org.name.toLowerCase().includes(orgSearchTerm.toLowerCase())
  );

  // Filter organizations for dialog dropdown
  const filteredDialogOrganizations = organizations.filter((org) =>
    org.name.toLowerCase().includes(dialogOrgSearchTerm.toLowerCase())
  );

  const handleResetSearch = () => {
    setSearchTerm("");
    setSelectedOrg("");
    setOrgSearchTerm("");
    setShowOrgDropdown(false);
    // No need to set filteredUsers - useMemo will handle it automatically
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".org-dropdown-container")) {
        setShowOrgDropdown(false);
      }
      if (!event.target.closest(".dialog-org-dropdown-container")) {
        setShowDialogOrgDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    setDeleteLoading(true);
    try {
      await deleteUserApi(userToDelete.id);

      // Update local state
      const updatedUsers = users.filter((u) => u.id !== userToDelete.id);
      setUsers(updatedUsers);

      setShowDeleteDialog(false);
      setUserToDelete(null);
      toast.success("User deleted successfully!");
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(error.message || "Error deleting user");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setUserToDelete(null);
  };

  const handleResetPassword = (user) => {
    // TODO: Implement password reset functionality
    toast.error("Password reset functionality not yet implemented");
  };

  const handleChangeUserType = (user) => {
    setSelectedUser(user);
    setNewUserType(user.user_type || "user");
    const currentOrgId = user.organization_id?.path
      ? user.organization_id.path.split("/").pop()
      : "";
    setNewOrganizationId(currentOrgId);
    setDialogOrgSearchTerm(
      currentOrgId ? getOrganizationName(user) : ""
    );
    setShowDialogOrgDropdown(false);
    setShowTypeDialog(true);
  };

  const handleSaveUserType = async () => {
    if (!selectedUser || !newUserType) return;

    try {
      const userRef = doc(db, "users", selectedUser.id);

      const updates = {
        user_type: newUserType,
        organization_id: newOrganizationId
          ? doc(db, "organizations", newOrganizationId)
          : null,
        organization_name: newOrganizationId
          ? organizations.find((o) => o.id === newOrganizationId)?.name || ""
          : "",
      };

      await updateDoc(userRef, updates);

      // Update local state
      const updatedUserData = {
        ...selectedUser,
        user_type: newUserType,
        organization_id: updates.organization_id,
        organization_name: updates.organization_name,
      };
      const updatedUsers = users.map((user) =>
        user.id === selectedUser.id ? updatedUserData : user
      );
      setUsers(updatedUsers);

      setShowTypeDialog(false);
      setSelectedUser(null);
      setNewUserType("");
      setNewOrganizationId("");
      setDialogOrgSearchTerm("");
      toast.success("User updated successfully!");
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error("Error updating user");
    }
  };

  const handleCancelUserType = () => {
    setShowTypeDialog(false);
    setSelectedUser(null);
    setNewUserType("");
    setNewOrganizationId("");
    setDialogOrgSearchTerm("");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="py-8 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <p className="mt-2 text-gray-600">Loading users...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="py-8 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-primary-600">Users</h1>
          </div>

          {/* Search and Filters */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Email Search */}
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search users by email..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Organization Filter */}
              <div className="w-full sm:w-80 relative org-dropdown-container">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search organizations..."
                    value={orgSearchTerm}
                    onChange={(e) => setOrgSearchTerm(e.target.value)}
                    onFocus={() => setShowOrgDropdown(true)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    disabled={orgsLoading}
                  />
                  {selectedOrg && (
                    <button
                      onClick={() => {
                        setSelectedOrg("");
                        setOrgSearchTerm("");
                        // No need to call applyFilters - useMemo will handle it automatically
                      }}
                      className="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      ×
                    </button>
                  )}
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">
                    <ChevronDownIcon className="w-4 h-4" />
                  </div>
                </div>

                {/* Autocomplete Dropdown */}
                {showOrgDropdown && !orgsLoading && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    {filteredOrganizations.length > 0 ? (
                      filteredOrganizations.map((org) => (
                        <div
                          key={org.id}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                          onClick={() => {
                            setSelectedOrg(org.id);
                            setOrgSearchTerm(org.name);
                            setShowOrgDropdown(false);
                            // No need to call applyFilters - useMemo will handle it automatically
                          }}
                        >
                          {org.name}
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-gray-500">
                        No organizations found
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Reset Button */}
              {(searchTerm || selectedOrg) && (
                <button
                  onClick={handleResetSearch}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Reset
                </button>
              )}
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {user.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getUserTypeBadgeClasses(
                          user.user_type
                        )}`}
                      >
                        {getUserTypeLabel(user.user_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {getOrganizationName(user)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleChangeUserType(user)}
                          className="text-blue-600 hover:text-blue-900 transition-colors p-1"
                          title="Update User"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleResetPassword(user)}
                          className="text-yellow-600 hover:text-yellow-900 transition-colors p-1"
                          title="Reset Password"
                        >
                          <KeyIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          className="text-red-600 hover:text-red-900 transition-colors p-1"
                          title="Delete User"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">
                {searchTerm || selectedOrg
                  ? "No users found matching your search criteria."
                  : "Use the search and filter options above to find users."}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Update User Dialog */}
      {showTypeDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-20 pb-8">
          <div className="relative p-5 border w-[28rem] shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Update User
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Updating: <strong>{selectedUser?.email}</strong>
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  User Type
                </label>
                <select
                  value={newUserType}
                  onChange={(e) => setNewUserType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>

              <div className="mb-4 relative dialog-org-dropdown-container">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search organizations..."
                    value={dialogOrgSearchTerm}
                    onChange={(e) => {
                      setDialogOrgSearchTerm(e.target.value);
                      setShowDialogOrgDropdown(true);
                    }}
                    onFocus={() => setShowDialogOrgDropdown(true)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    disabled={orgsLoading}
                  />
                  {newOrganizationId && (
                    <button
                      type="button"
                      onClick={() => {
                        setNewOrganizationId("");
                        setDialogOrgSearchTerm("");
                      }}
                      className="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      ×
                    </button>
                  )}
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                    <ChevronDownIcon className="w-4 h-4" />
                  </div>
                </div>
                {showDialogOrgDropdown && !orgsLoading && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-auto">
                    <div
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 text-gray-500"
                      onClick={() => {
                        setNewOrganizationId("");
                        setDialogOrgSearchTerm("");
                        setShowDialogOrgDropdown(false);
                      }}
                    >
                      — No organization —
                    </div>
                    {filteredDialogOrganizations.map((org) => (
                      <div
                        key={org.id}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                        onClick={() => {
                          setNewOrganizationId(org.id);
                          setDialogOrgSearchTerm(org.name);
                          setShowDialogOrgDropdown(false);
                        }}
                      >
                        {org.name}
                      </div>
                    ))}
                    {filteredDialogOrganizations.length === 0 && (
                      <div className="px-4 py-2 text-gray-500">
                        {dialogOrgSearchTerm
                          ? `No organizations match "${dialogOrgSearchTerm}"`
                          : "No organizations found"}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancelUserType}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveUserType}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Dialog */}
      {showDeleteDialog && userToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-20 pb-8">
          <div className="relative p-5 border w-[28rem] shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Delete User
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to delete{" "}
                <strong>{userToDelete.email}</strong>?
              </p>
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4">
                This will remove the user from both the database and Firebase
                Authentication. This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancelDelete}
                  disabled={deleteLoading}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={deleteLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteLoading ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
